import {Component, OnInit} from '@angular/core';
import {AlertService} from "../services";
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import {MatDialog} from "@angular/material/dialog";
import {ComponentTitleService} from "../services/componentTitle.service";
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";

import { locale as polish } from './i18n/pl';
import {SupplierService} from "../services/supplier.service";
import {SupplierOrderService} from "../services/supplierOrder.service";
import {SupplierDialogComponent} from "../supplier/dialog/supplierDialog.component";
import {Product} from "../models/product";
import {ProductService} from "../services/product.service";
import {UnitService} from "../services/unit.services";
import {VatRateService} from "../services/vatRate.service";
import {Unit} from "../models/unit";
import {VatRate} from "../models/vatRate";
import {Supplier} from "../models/supplier";
import {fuseAnimations} from "../../../@fuse/animations";
import {MatSnackBar} from "@angular/material/snack-bar";

@Component({
    templateUrl: './supplierOrderForm.component.html',
    styleUrls: ['./supplierOrderForm.component.scss'],
    animations   : fuseAnimations
})

export class SupplierOrderFormComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    isAddMode: boolean;
    suppliers: Supplier[];
    dialogRef: any;
    products: Product[];
    units: Unit[];
    vatRates: VatRate[];

    constructor(
        public _matDialog: MatDialog,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private supplierService: SupplierService,
        private supplierOrderService: SupplierOrderService,
        private productService: ProductService,
        private unitService: UnitService,
        private vatRateService: VatRateService,
        private _matSnackBar: MatSnackBar,
    ) {
        this.translationLoader.loadTranslations(polish);

        componentTitleService.clearTitle();
        componentTitleService.addPart('Supplier orders');
    }

    ngOnInit() {
        this.supplierService.getAll().subscribe((res: any[]) => {
            this.suppliers = res;
        }, error => {
            this.alertService.error(error);
        });

        this.productService.getAll().subscribe((res: Product[]) => {
            this.products = res;
        }, error => {
            this.alertService.error(error);
        });

        this.vatRateService.getAll().subscribe((res: VatRate[]) => {
            this.vatRates = res;
        }, error => {
            this.alertService.error(error);
        });

        this.unitService.getAll().subscribe((res: Unit[]) => {
            this.units = res;
        }, error => {
            this.alertService.error(error);
        });

        this.id = this.route.snapshot.params['id'];
        this.isAddMode = !this.id;

        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            supplier: ['', Validators.required],
            invoiceNumber: ['', Validators.required],
            documentDate: [''],
            description: ['', Validators.required],
            items: this.formBuilder.array([]),
        });

        if (!this.isAddMode) {
            this.supplierOrderService.getById(this.id)
                .pipe(first())
                .subscribe(x => {
                    this.f.supplier.setValue(x.supplier);
                    this.f.invoiceNumber.setValue(x.invoiceNumber);
                    this.f.description.setValue(x.description);
                });
        } else {
            this.addLine();
        }
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        // this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createSupplierOrder();
        } else {
            this.updateSupplierOrder();
        }
    }

    public addSupplier() {
        this.dialogRef = this._matDialog.open(SupplierDialogComponent, {
            panelClass: 'supplier-form-dialog',
            data      : {
                action : 'new'
            }
        });

        this.dialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if ( !response )
                {
                    return;
                }

                let supplier = response.getRawValue();

                this.supplierService.create(supplier)
                    .pipe(first())
                    .subscribe(
                        (data:Supplier)=> {
                            this.alertService.success('Supplier added successfully');
                            this.suppliers.push(data);
                        },
                        error => {
                            this.alertService.error(error);
                            this.loading = false;
                        });

            }, (err) => {
                console.log(err);
            });
    }

    newLine(): FormGroup {
        return this.formBuilder.group({
            id: [''],
            product: ['', Validators.required],
            unit: ['', Validators.required],
            quantity: ['', Validators.required],
            vatRate: ['', Validators.required],
            priceNet: ['', Validators.required],
            priceGross: ['', Validators.required],
        });
    }

    public addLine() {
        this.items().push(this.newLine());
    }

    public removeLine(lineIdx: number) {
        this.items().removeAt(lineIdx);
    }

    public items(): FormArray {
        return this.form.get('items') as FormArray;
    }

    public onProductSelect(lineIdx: number, event: any) {
        for (let i = 0; i < this.products.length; i++) {
            if (this.products[i].id != event.value) {
                continue;
            }

            if (typeof this.products[i].unit !== "undefined" && this.products[i].unit.id !== null) {
                this.items().at(lineIdx).get('unit').setValue(this.products[i].unit.id);
            }

            if (typeof this.products[i].vatRate !== "undefined" && this.products[i].vatRate != null) {
                this.items().at(lineIdx).get('vatRate').setValue(this.products[i].vatRate.vatValue);
            }

            if (typeof this.products[i].purchasePriceNet !== "undefined" && this.products[i].purchasePriceNet != null) {
                this.items().at(lineIdx).get('priceNet').setValue(this.products[i].purchasePriceNet);
            }

            if (typeof this.products[i].purchasePriceGross !== "undefined" && this.products[i].purchasePriceGross != null) {
                this.items().at(lineIdx).get('priceGross').setValue(this.products[i].purchasePriceGross);
            }

            break;
        }
    }

    private createSupplierOrder() {
        this.supplierOrderService.create(this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Supplier added successfully');
                    this.router.navigate(['.', { relativeTo: this.route }]);
                },
                error => {
                    this.loading = false;
                    this._matSnackBar.open('Supplier order creation error - ' + error, 'OK', {
                        verticalPosition: 'top',
                        duration        : 5000,
                        panelClass: ['snackbar-error']
                    });
                });
    }

    private updateSupplierOrder() {
        this.supplierOrderService.update(this.id, this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Update successful');
                    this.router.navigate(['..', { relativeTo: this.route }]);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }
}
